import { parseEnvelope, type Envelope } from "./envelope";
import { AppError, networkError, problemToError } from "./problem";
import { holdRelease } from "../release/holds";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
  /** Attached as `Idempotency-Key`. Endpoints that require it pass one explicitly. */
  idempotencyKey?: string;
  /** Attached as `If-Match` for conflict-prone writes. */
  ifMatch?: string;
  /** Auto-attach the CSRF header. Defaults to true for mutations, false for GET. */
  csrf?: boolean;
  /** Single automatic retry on network error / 503. Defaults to true for GET only. */
  retryOnUnavailable?: boolean;
  headers?: Record<string, string>;
  /** `none` for 204/empty responses. */
  expect?: "json" | "none";
}

export interface ApiResponse<D = unknown, M = unknown> {
  data: D;
  meta: M;
  etag: string | null;
  status: number;
  correlationId: string;
}

// ---- CSRF: synchronizer token kept in memory only (never storage). ----
let csrfToken: string | null = null;
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}
export function getCsrfToken(): string | null {
  return csrfToken;
}

// When the server reports `csrf_invalid`, we notify a handler (the session store) so
// it can silently re-fetch `/v1/session` for a fresh token; the user still resubmits.
let onCsrfInvalid: (() => void) | null = null;
export function setCsrfInvalidHandler(handler: (() => void) | null): void {
  onCsrfInvalid = handler;
}

// Notified on any 401 so the app can drop to the signed-out state.
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

// The update detector observes the cheap release header on every response. The
// callback deliberately receives only the opaque web component id exposed by
// the server; it fetches the full release record before changing UI state.
let onReleaseObserved: ((releaseId: string) => void) | null = null;
export function setReleaseObserver(handler: ((releaseId: string) => void) | null): void {
  onReleaseObserved = handler;
}

const MUTATIONS = new Set<HttpMethod>(["POST", "PUT", "PATCH", "DELETE"]);

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

function readRetryAfter(headers: Headers): number | undefined {
  const raw = headers.get("retry-after");
  if (!raw) return undefined;
  const seconds = Number(raw);
  return Number.isFinite(seconds) ? seconds : undefined;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
      },
      { once: true }
    );
  });
}

async function attempt<D, M>(
  path: string,
  opts: RequestOptions,
  correlationId: string
): Promise<ApiResponse<D, M>> {
  const method = opts.method ?? "GET";
  const isMutation = MUTATIONS.has(method);
  const headers = new Headers(opts.headers);
  headers.set("accept", "application/json");
  headers.set("x-epidev-request-id", correlationId);

  const useCsrf = opts.csrf ?? isMutation;
  if (useCsrf && csrfToken) headers.set("x-csrf-token", csrfToken);
  if (opts.idempotencyKey) headers.set("idempotency-key", opts.idempotencyKey);
  if (opts.ifMatch) headers.set("if-match", opts.ifMatch);

  const init: RequestInit = {
    method,
    credentials: "include",
    headers,
    // Belt-and-braces: fetch-metadata already enforced server-side, but be explicit.
    mode: "same-origin",
    ...(opts.signal ? { signal: opts.signal } : {})
  };
  if (opts.body !== undefined) {
    headers.set("content-type", "application/json");
    init.body = JSON.stringify(opts.body);
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, opts.query), init);
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw networkError(correlationId);
  }

  const observedRelease = response.headers.get("x-portal-release")?.trim();
  if (observedRelease) onReleaseObserved?.(observedRelease);

  const etag = response.headers.get("etag");
  const serverCorrelation = response.headers.get("x-request-id") || correlationId;

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      /* problem body may be absent */
    }
    const error = problemToError(body, response.status, serverCorrelation, readRetryAfter(response.headers));
    if (error.kind === "csrf") onCsrfInvalid?.();
    if (error.status === 401) onUnauthorized?.();
    throw error;
  }

  if (opts.expect === "none" || response.status === 204) {
    return { data: undefined as D, meta: {} as M, etag, status: response.status, correlationId: serverCorrelation };
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new AppError({
      title: "Unexpected response",
      status: 502,
      detail: "The server returned an unexpected response.",
      code: "invalid_json",
      correlationId: serverCorrelation
    });
  }

  const envelope: Envelope<D, M> = parseEnvelope<D, M>(body, serverCorrelation);
  return { data: envelope.data, meta: envelope.meta, etag, status: response.status, correlationId: serverCorrelation };
}

export async function request<D = unknown, M = unknown>(
  path: string,
  opts: RequestOptions = {}
): Promise<ApiResponse<D, M>> {
  const method = opts.method ?? "GET";
  const correlationId = crypto.randomUUID();
  const releaseMutationHold = MUTATIONS.has(method) ? holdRelease("mutation") : null;
  // Reads are safely retried once on transport failure / unavailability; mutations
  // are NEVER auto-retried (a manual retry reuses the same Idempotency-Key instead).
  const canRetry = (opts.retryOnUnavailable ?? method === "GET") && !MUTATIONS.has(method);

  try {
    try {
      return await attempt<D, M>(path, opts, correlationId);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") throw error;
      const isRetryable = error instanceof AppError && (error.kind === "network" || error.status === 503);
      if (canRetry && isRetryable) {
        await sleep(400, opts.signal);
        return attempt<D, M>(path, opts, correlationId);
      }
      throw error;
    }
  } finally {
    releaseMutationHold?.();
  }
}
